import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Segmented } from 'antd';
import {
  SendOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { submitGlp1BridgeFeedback } from '../../../api/glp1BridgeChat';
import '../Glp1BridgeAssistant.css';

import AskEmptyState from './components/AskEmptyState';
import AssistantLoadingMessage from './components/AssistantLoadingMessage';
import BridgeScreeningPanel from './components/BridgeScreeningPanel';
import ContextBanner from './components/ContextBanner';
import GuidedBridgeEligibilityFrame from './components/GuidedBridgeEligibilityFrame';
import MessageBubble from './components/MessageBubble';
import PromptRail from './components/PromptRail';
import ScreeningStatusPanel from './components/ScreeningStatusPanel';

import { useStreamChat } from './hooks/useStreamChat';

import { composeAnswer } from './utils/composeAnswer';
import {
  initialMessages,
  screeningFollowUpQuestions,
  suggestedQuestions,
  PHI_BLOCKED_ANSWER,
  PHI_BLOCKED_USER_MESSAGE,
} from './utils/constants';
import { formatScreeningSummary, getInitialScreeningValues } from './utils/screening';
import { looksLikePhiClient } from './utils/phi';

const { TextArea } = Input;
const screenerViewOptions = [
  { label: 'Guided screener', value: 'guided' },
  // Hidden for now; keep for a future checklist relaunch.
  // { label: 'Clinical checklist', value: 'checklist' },
];

export default function Glp1BridgeChatPanel({ activeMode = 'screener', onModeChange, onAccessRequired }) {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState(initialMessages);
  const [loading, setLoading] = useState(false);
  const [screeningValues, setScreeningValues] = useState(getInitialScreeningValues);
  const [screenerView, setScreenerView] = useState('guided');
  const [lastScreeningContext, setLastScreeningContext] = useState(null);
  const transcriptRef = useRef(null);
  const screenerRef = useRef(null);
  // Keep a live ref to the messages array so callbacks (copy, print) can
  // walk the transcript without depending on `messages` and re-creating
  // every render. Re-creating would invalidate React.memo on MessageBubble.
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const { streamAbortRef, runStream } = useStreamChat({ setMessages, setLastScreeningContext });

  useEffect(() => () => {
    if (streamAbortRef.current) {
      try { streamAbortRef.current.abort(); } catch (e) { /* ignore */ }
    }
  }, [streamAbortRef]);

  // Walk back from a given message to find the user question that prompted
  // it. Skips system/error messages and PHI-blocked turns (those carry no
  // useful question text).
  const findPrecedingUserMessage = useCallback((message) => {
    const all = messagesRef.current;
    if (!Array.isArray(all)) return null;
    const idx = all.findIndex((m) => m.id === message.id);
    if (idx <= 0) return null;
    for (let i = idx - 1; i >= 0; i--) {
      const m = all[i];
      if (m.sender !== 'user') continue;
      if (m.privacy_blocked) return null;
      return m;
    }
    return null;
  }, []);

  const switchToAsk = useCallback(() => {
    onModeChange?.('ask');
  }, [onModeChange]);

  const switchToScreener = useCallback(() => {
    onModeChange?.('screener');
  }, [onModeChange]);

  const stopStream = useCallback(() => {
    if (streamAbortRef.current) {
      try { streamAbortRef.current.abort(); } catch (e) { /* ignore */ }
    }
    setMessages((current) =>
      current.map((m) =>
        m.streaming
          ? { ...m, streaming: false, stopped_by_user: true, grounding_state: 'skipped' }
          : m
      )
    );
    setLoading(false);
  }, [streamAbortRef]);

  // Match the engine's MAX_CONVERSATION_TURNS in glp1_bridge/engine.py. The
  // v1 cap of 6 turns was a holdover from the old retrieval engine that
  // couldn't handle longer histories. v2 uses prompt caching, so extra
  // history is cheap (a few hundred bytes per turn, billed at cache-read
  // rates after the first call).
  const conversationForApi = useMemo(
    () =>
      messages
        .filter((message) => !message.privacy_excluded_from_api)
        .slice(-20)
        .map((message) => ({
          role: message.sender === 'user' ? 'user' : 'assistant',
          content:
            message.sender === 'assistant'
              ? composeAnswer(message.direct_answer || '', message.body_markdown || '') || message.answer || ''
              : message.answer,
        })),
    [messages]
  );

  const latestAssistantId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.sender === 'assistant' && !m.streaming) {
        return m.id;
      }
    }
    return null;
  }, [messages]);

  useEffect(() => {
    if (activeMode !== 'ask') return;
    const transcript = transcriptRef.current;
    if (!transcript) return;

    if (loading) {
      transcript.scrollTop = transcript.scrollHeight;
      return;
    }

    const lastMessage = messages[messages.length - 1];
    const messageNode = lastMessage ? transcript.querySelector(`[data-message-id="${lastMessage.id}"]`) : null;
    if (messageNode) {
      transcript.scrollTop = Math.max(0, messageNode.offsetTop - transcript.offsetTop - 6);
      return;
    }

    transcript.scrollTop = transcript.scrollHeight;
  }, [messages, loading, activeMode]);

  useEffect(() => {
    const startScreener = () => switchToScreener();
    window.addEventListener('glp1-bridge-start-screener', startScreener);
    return () => window.removeEventListener('glp1-bridge-start-screener', startScreener);
  }, [switchToScreener]);

  useEffect(() => {
    if (activeMode === 'screener' && screenerRef.current) {
      screenerRef.current.scrollTop = 0;
    }
  }, [activeMode]);

  const submitQuestion = async (questionText = question) => {
    const trimmedQuestion = String(questionText || '').trim();
    if (!trimmedQuestion || loading) return;

    if (looksLikePhiClient(trimmedQuestion)) {
      const now = new Date().toISOString();
      setMessages((currentMessages) => [
        ...currentMessages,
        {
          id: `user-blocked-${Date.now()}`,
          sender: 'user',
          answer: PHI_BLOCKED_USER_MESSAGE,
          privacy_blocked: true,
          privacy_excluded_from_api: true,
          created_at: now,
        },
        {
          id: `assistant-privacy-${Date.now()}`,
          sender: 'assistant',
          answer: PHI_BLOCKED_ANSWER,
          error: 'phi_not_allowed',
          citations: [],
          confidence: 'low',
          not_final_coverage_determination: true,
          created_at: now,
        },
      ]);
      setQuestion('');
      return;
    }

    const msgNow = new Date().toISOString();
    const userMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      answer: trimmedQuestion,
      created_at: msgNow,
    };
    const assistantId = `assistant-${Date.now()}`;
    const assistantPlaceholder = {
      id: assistantId,
      sender: 'assistant',
      answer: '',
      direct_answer: '',
      body_markdown: '',
      citations: [],
      confidence: null,
      streaming: true,
      phase: 'opening',
      grounding_state: 'pending',
      not_final_coverage_determination: true,
      created_at: msgNow,
    };
    setMessages((currentMessages) => [...currentMessages, userMessage, assistantPlaceholder]);
    setQuestion('');
    setLoading(true);

    try {
      const { promise } = runStream({
        assistantId,
        requestBody: {
          question: trimmedQuestion,
          conversation: conversationForApi,
          screeningContext: lastScreeningContext,
        },
        onDone: () => {
          // Suggest the screener if v2 flagged the question as patient-specific.
          setMessages((current) => {
            const last = current.find((m) => m.id === assistantId);
            if (last?.suggests_screener && activeMode === 'ask') {
              // Don't auto-switch -- let the user decide via the suggestion button.
            }
            return current;
          });
        },
      });
      await promise;
    } catch (error) {
      if (error?.code === 'provider_access_required') {
        onAccessRequired?.();
        return;
      }
      if (error?.name === 'AbortError') {
        // User started a new question; the stream was deliberately cancelled.
        return;
      }
      setMessages((current) =>
        current.map((m) =>
          m.id === assistantId
            ? {
                ...m,
                answer: error?.message || 'Unable to answer this question right now.',
                error: error?.code || 'stream_failed',
                streaming: false,
                grounding_state: 'skipped',
              }
            : m
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const submitScreening = async () => {
    if (loading) return;

    const screenNow = new Date().toISOString();
    const userMessage = {
      id: `user-screening-${Date.now()}`,
      sender: 'user',
      answer: formatScreeningSummary(screeningValues),
      created_at: screenNow,
    };
    const assistantId = `assistant-screening-${Date.now()}`;
    const assistantPlaceholder = {
      id: assistantId,
      sender: 'assistant',
      answer: '',
      direct_answer: '',
      body_markdown: '',
      citations: [],
      confidence: null,
      streaming: true,
      phase: 'opening',
      grounding_state: 'skipped', // deterministic screener doesn't need grounding
      not_final_coverage_determination: true,
      created_at: screenNow,
    };
    setMessages((currentMessages) => [...currentMessages, userMessage, assistantPlaceholder]);
    setLoading(true);

    try {
      const { promise } = runStream({
        assistantId,
        requestBody: {
          question: 'Run GLP-1 Bridge screening with structured de-identified inputs.',
          conversation: conversationForApi,
          screening: screeningValues,
          screeningContext: lastScreeningContext,
        },
        onScreenerComplete: (screening) => {
          if (screening) switchToAsk();
        },
      });
      await promise;
    } catch (error) {
      if (error?.code === 'provider_access_required') {
        onAccessRequired?.();
        return;
      }
      if (error?.name === 'AbortError') return;
      setMessages((current) =>
        current.map((m) =>
          m.id === assistantId
            ? {
                ...m,
                answer: error?.message || 'Unable to run screening right now.',
                error: error?.code || 'screener_failed',
                streaming: false,
              }
            : m
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const resetScreeningValues = () => {
    setScreeningValues(getInitialScreeningValues());
  };

  const submitFeedback = useCallback(async (message, feedbackType) => {
    if (!message?.id || (!message.chat_log_id && !message.request_id)) return;
    setMessages((currentMessages) =>
      currentMessages.map((currentMessage) =>
        currentMessage.id === message.id
          ? { ...currentMessage, feedback_status: 'sending', feedback_type: feedbackType }
          : currentMessage
      )
    );
    try {
      await submitGlp1BridgeFeedback({
        chatLogId: message.chat_log_id,
        requestId: message.request_id,
        feedbackType,
      });
      setMessages((currentMessages) =>
        currentMessages.map((currentMessage) =>
          currentMessage.id === message.id
            ? { ...currentMessage, feedback_status: 'submitted', feedback_type: feedbackType }
            : currentMessage
        )
      );
    } catch (error) {
      if (error.code === 'provider_access_required') {
        onAccessRequired?.();
        return;
      }
      setMessages((currentMessages) =>
        currentMessages.map((currentMessage) =>
          currentMessage.id === message.id
            ? { ...currentMessage, feedback_status: 'error', feedback_type: feedbackType }
            : currentMessage
        )
      );
    }
  }, [onAccessRequired]);

  // Live timestamp tick -- re-renders every 30s so relative times stay current.
  const [, setNow] = useState(0);
  useEffect(() => {
    if (messages.length === 0) return undefined;
    const id = setInterval(() => setNow((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, [messages.length]);

  const copyAnswerToClipboard = useCallback(async (message) => {
    const userMessage = findPrecedingUserMessage(message);
    const questionBlock = userMessage && userMessage.answer
      ? `Question:\n${userMessage.answer.trim()}\n\n`
      : '';
    const direct = (message.direct_answer || '').trim();
    const body = (message.body_markdown || message.answer || '').trim();
    const answerBody = [direct, body].filter(Boolean).join('\n\n');
    const answerBlock = answerBody ? `Answer:\n${answerBody}` : '';
    const citationLines = (message.citations || []).map((c) => {
      const title = c.source_title || c.title || c.source_id || 'source';
      const loc = c.locator ? ` (${c.locator})` : '';
      return `- ${title}${loc}`;
    });
    const sourcesBlock = citationLines.length ? `\n\nSources:\n${citationLines.join('\n')}` : '';
    const text = `${questionBlock}${answerBlock}${sourcesBlock}`.trim();
    try {
      await navigator.clipboard.writeText(text);
      setMessages((current) =>
        current.map((m) => (m.id === message.id ? { ...m, copy_state: 'copied' } : m))
      );
      setTimeout(() => {
        setMessages((current) =>
          current.map((m) => (m.id === message.id ? { ...m, copy_state: undefined } : m))
        );
      }, 1500);
    } catch (err) {
      setMessages((current) =>
        current.map((m) => (m.id === message.id ? { ...m, copy_state: 'error' } : m))
      );
    }
  }, [findPrecedingUserMessage]);

  const printMessage = useCallback((message) => {
    // Print the answer together with the question that prompted it so the
    // output reads as a complete record, not an orphan reply.
    const userMessage = findPrecedingUserMessage(message);
    const targetIds = new Set([message.id]);
    if (userMessage) targetIds.add(userMessage.id);
    setMessages((current) =>
      current.map((m) => {
        const shouldTarget = targetIds.has(m.id);
        return m.print_target === shouldTarget ? m : { ...m, print_target: shouldTarget };
      })
    );
    document.body.classList.add('glp1-printing');
    setTimeout(() => {
      window.print();
      requestAnimationFrame(() => {
        document.body.classList.remove('glp1-printing');
        setMessages((current) =>
          current.map((m) => (m.print_target ? { ...m, print_target: false } : m))
        );
      });
    }, 50);
  }, [findPrecedingUserMessage]);

  const visibleSuggestedQuestions = lastScreeningContext ? screeningFollowUpQuestions : suggestedQuestions;
  const hasMessages = messages.length > 0;

  return (
    <main className={`glp1-assistant-main glp1-assistant-main--${activeMode}`}>
      <section className="glp1-workspace">
        {activeMode === 'screener' ? (
          <section
            ref={screenerRef}
            className={`glp1-screener-layout glp1-screener-layout--${screenerView}`}
            aria-label="Eligibility Screener"
          >
            <div className="glp1-screener-modebar">
              <Segmented
                value={screenerView}
                onChange={(value) => setScreenerView(value)}
                options={screenerViewOptions}
              />
            </div>
            {screenerView === 'guided' ? (
              <GuidedBridgeEligibilityFrame />
            ) : (
              <>
                <BridgeScreeningPanel
                  values={screeningValues}
                  onChange={setScreeningValues}
                  onSubmit={submitScreening}
                  onReset={resetScreeningValues}
                  loading={loading}
                />
                <ScreeningStatusPanel
                  values={screeningValues}
                  lastScreeningContext={lastScreeningContext}
                  onAskFollowUp={switchToAsk}
                  onRunScreening={submitScreening}
                  loading={loading}
                />
              </>
            )}
          </section>
        ) : (
          <section className={`glp1-chat-panel${hasMessages ? ' glp1-chat-panel--has-messages' : ' glp1-chat-panel--empty'}`} aria-label="Ask Bridge Question">
            <ContextBanner screening={lastScreeningContext} onOpenScreener={switchToScreener} />
            {!hasMessages ? (
              <AskEmptyState
                onOpenScreener={switchToScreener}
                questions={visibleSuggestedQuestions.slice(0, 3)}
                onSelectQuestion={submitQuestion}
                loading={loading}
              />
            ) : null}

            {hasMessages ? (
              <div ref={transcriptRef} data-glp1-transcript="true" className="glp1-transcript">
                {messages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    isLatest={message.id === latestAssistantId}
                    onFeedback={submitFeedback}
                    onOpenScreener={switchToScreener}
                    onCopyAnswer={copyAnswerToClipboard}
                    onPrintMessage={printMessage}
                  />
                ))}
                {loading && (() => {
                  const streamingMsg = [...messages].reverse().find((m) => m.sender === 'assistant' && m.streaming);
                  if (streamingMsg && (streamingMsg.direct_answer || streamingMsg.body_markdown)) {
                    return null; // text is already flowing; the real bubble takes over
                  }
                  return <AssistantLoadingMessage phase={streamingMsg?.phase || 'opening'} />;
                })()}
                {!loading ? (
                  <div className="glp1-followups">
                    <PromptRail
                      label="Suggested follow-ups"
                      questions={visibleSuggestedQuestions}
                      onSelect={submitQuestion}
                      loading={loading}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className={`glp1-composer${hasMessages ? '' : ' glp1-composer--static'}`}>
              <div className="glp1-composer-input-row">
                <TextArea
                  value={question}
                  onChange={(event) => setQuestion(event.target.value)}
                  autoSize={{ minRows: 1, maxRows: 6 }}
                  maxLength={2400}
                  placeholder={'Ask with de-identified details only — e.g., "BMI 32 with HFpEF — Bridge candidate?"'}
                  disabled={loading}
                  onPressEnter={(event) => {
                    if (!event.shiftKey) {
                      event.preventDefault();
                      submitQuestion();
                    }
                  }}
                />
                {loading ? (
                  <Button
                    type="default"
                    icon={<StopOutlined />}
                    aria-label="Stop generating"
                    className="glp1-send-button glp1-stop-button"
                    onClick={stopStream}
                  />
                ) : (
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    aria-label="Send question"
                    className="glp1-send-button"
                    disabled={!question.trim()}
                    onClick={() => submitQuestion()}
                  />
                )}
              </div>
            </div>
          </section>
        )}
      </section>
    </main>
  );
}
